import { useEffect, useState } from 'react'
import { toast } from 'react-hot-toast'
import { formatCurrency } from '../../utils/formatters'
import { authService } from '../../services/auth.service'

interface MembershipInfoCardProps {
  memberId?: string
  walletNumber?: string | null
  virtualAccountNumber?: string | null
  virtualAccountBank?: string | null
  /** Informational VA balance (RM) — read-only here; Finance/Admin maintain it. */
  vaBalance?: number | null
  /** This user's own 6-char referral code — shareable at signup. */
  referralCode?: string
}

export default function MembershipInfoCard({
  memberId,
  walletNumber,
  virtualAccountNumber,
  virtualAccountBank,
  vaBalance,
  referralCode,
}: MembershipInfoCardProps) {
  const hasWallet = Boolean(walletNumber)
  const hasVa = Boolean(virtualAccountNumber)
  const hasVaBalance = vaBalance !== null && vaBalance !== undefined
  const [copied, setCopied] = useState(false)
  // Same per-environment flag as the registration form (REFERRALS_ENABLED) —
  // hides the row in production where the program is off. Fails open so a
  // status-fetch hiccup never hides a working feature.
  const [referralsEnabled, setReferralsEnabled] = useState(true)
  useEffect(() => {
    authService
      .referralStatus()
      .then((r) => setReferralsEnabled(!!r.enabled))
      .catch(() => undefined)
  }, [])

  const copyReferralCode = async () => {
    if (!referralCode) return
    try {
      await navigator.clipboard.writeText(referralCode)
      setCopied(true)
      toast.success('Referral code copied')
      setTimeout(() => setCopied(false), 1500)
    } catch {
      toast.error('Could not copy the code')
    }
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-100">
        <h3 className="text-base font-semibold text-gray-900">Membership Information</h3>
        <p className="text-xs text-gray-500 mt-0.5">Your account identifiers on the platform</p>
      </div>
      <dl className="divide-y divide-gray-100">
        <Row label="User ID" value={memberId || '—'} />
        <Row
          label="Wallet ID"
          value={walletNumber || 'Not assigned'}
          muted={!hasWallet}
        />
        <Row
          label="Virtual Account"
          value={
            hasVa
              ? virtualAccountBank
                ? `${virtualAccountBank} · ${virtualAccountNumber}`
                : virtualAccountNumber!
              : undefined
          }
          muted={!hasVa}
          badge={hasVa ? undefined : 'Coming Soon'}
        />
        <Row
          label="VA Balance"
          value={hasVaBalance ? formatCurrency(vaBalance!) : undefined}
          muted={!hasVaBalance}
          badge={hasVaBalance ? undefined : 'Not Set'}
        />
        {referralsEnabled && (
          <div className="flex items-center justify-between px-5 py-3">
            <dt className="text-sm font-medium text-gray-600">Referral Code</dt>
            <dd>
              {referralCode ? (
                <button
                  type="button"
                  onClick={copyReferralCode}
                  title="Copy referral code"
                  className="text-sm font-mono text-gray-900 underline decoration-dotted underline-offset-4 hover:text-[#0D7330]"
                >
                  {referralCode}
                  <span className="ml-2 text-xs font-sans text-gray-400">
                    {copied ? 'Copied!' : 'Copy'}
                  </span>
                </button>
              ) : (
                <span className="text-sm text-gray-400">—</span>
              )}
            </dd>
          </div>
        )}
      </dl>
    </div>
  )
}

function Row({
  label,
  value,
  muted = false,
  badge,
}: {
  label: string
  value?: string
  muted?: boolean
  badge?: string
}) {
  return (
    <div className="flex items-center justify-between px-5 py-3">
      <dt className="text-sm font-medium text-gray-600">{label}</dt>
      <dd className="flex items-center gap-2">
        {badge && (
          <span className="inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-700 ring-1 ring-inset ring-amber-200">
            {badge}
          </span>
        )}
        {value && (
          <span
            className={`text-sm font-mono ${
              muted ? 'text-gray-400 italic' : 'text-gray-900'
            }`}
          >
            {value}
          </span>
        )}
      </dd>
    </div>
  )
}
